import {
  DELEGATION_REPORT_READ_ACTIVITY_KIND,
  DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND,
  type OrchestrationLatestTurn,
  type OrchestrationMessage,
  type OrchestrationProposedPlan,
  type OrchestrationThreadActivity,
} from "@taskin-code/contracts";

export interface ThreadSummaryMetadata {
  latestUserMessageAt: string | null;
  hasPendingApprovals: boolean;
  hasPendingUserInput: boolean;
  hasActionableProposedPlan: boolean;
  /**
   * Newest `delegation.report.read` activity on this thread, or null.
   *
   * Derived here rather than read from a column so it reaches both sides of the
   * wire through the one path that already carries derived thread state: the
   * server projects it onto the shell, the web recomputes it on the threads it
   * holds in full, and `hasUnreadDelegateReport` gets the same answer either
   * way.
   */
  delegateReportReadAt: string | null;
  /**
   * Newest `delegation.wake.skipped` activity on this thread, or null.
   *
   * This is the server saying it will not resume this thread again on its own —
   * the one case where a finished delegation report actually needs a person.
   * Travels the same derived-state path as `delegateReportReadAt` because the
   * sidebar has to paint it for threads it never held in full, and an activity
   * list that was trimmed would otherwise lose the only trace of the decision.
   */
  delegationWakeSkippedAt: string | null;
}

/**
 * The only activity kinds `deriveThreadSummaryMetadata` reads; every other kind
 * falls through its loop untouched.
 *
 * Exported so the server can ask the database for just these rows when it
 * builds a thread shell. Reading the whole activity table instead — every tool
 * call's payload of every thread, archived ones included — is what made the
 * shell snapshot outgrow the client's request timeout and left the sidebar
 * spinning forever. A kind added to the loop below must be added here too, or
 * the shell will silently stop seeing it.
 */
export const THREAD_SUMMARY_ACTIVITY_KINDS = [
  DELEGATION_REPORT_READ_ACTIVITY_KIND,
  DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND,
  "approval.requested",
  "approval.resolved",
  "provider.approval.respond.failed",
  "user-input.requested",
  "user-input.resolved",
  "provider.user-input.respond.failed",
] as const;

function maxIso(left: string | null, right: string): string {
  if (left === null) {
    return right;
  }
  return left > right ? left : right;
}

function compareActivitiesByOrder(
  left: Pick<OrchestrationThreadActivity, "createdAt" | "id" | "sequence">,
  right: Pick<OrchestrationThreadActivity, "createdAt" | "id" | "sequence">,
): number {
  const leftSequence = left.sequence ?? Number.MAX_SAFE_INTEGER;
  const rightSequence = right.sequence ?? Number.MAX_SAFE_INTEGER;
  return (
    leftSequence - rightSequence ||
    left.createdAt.localeCompare(right.createdAt) ||
    left.id.localeCompare(right.id)
  );
}

function toPayloadRecord(payload: unknown): Record<string, unknown> | null {
  return payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
}

function requestKindFromRequestType(
  requestType: unknown,
): "command" | "file-read" | "file-change" | null {
  switch (requestType) {
    case "command_execution_approval":
    case "exec_command_approval":
      return "command";
    case "file_read_approval":
      return "file-read";
    case "file_change_approval":
    case "apply_patch_approval":
      return "file-change";
    default:
      return null;
  }
}

function isStalePendingRequestFailureDetail(detail: string | undefined): boolean {
  if (!detail) {
    return false;
  }
  const normalized = detail.toLowerCase();
  return (
    normalized.includes("stale pending approval request") ||
    normalized.includes("unknown pending approval request") ||
    normalized.includes("stale pending user input request") ||
    normalized.includes("unknown pending user input request")
  );
}

/** What a turn must expose to say whether its pending requests still stand. */
export interface PendingRequestTurnView {
  readonly turnId: string;
  readonly completedAt?: string | null | undefined;
}

/**
 * Whether an approval or question asked by `requestTurnId` can still be answered.
 *
 * A provider only waits while the turn that asked is running. When that turn
 * ends — normally, interrupted, or killed along with the process on a restart —
 * nobody is listening any more: answering gets "stale pending request" back, and
 * the request is a leftover in the transcript rather than something owed to the
 * user.
 *
 * Counting leftovers as open is what left a delegating agent asleep forever: a
 * question that died with a deploy kept `hasPendingUserInput` true, and
 * `decideDelegationWake` reads that as "the user is the one being waited on" and
 * refuses to wake the parent — for the rest of that thread's life, with no log
 * and no activity to show why.
 *
 * A request with no turn of its own is judged by the live turn alone: it cannot
 * be attributed, and treating an unattributed request as dead would drop a real
 * prompt from a provider that does not stamp its turns.
 */
export function isPendingRequestAnswerable(
  requestTurnId: string | null | undefined,
  latestTurn: PendingRequestTurnView | null | undefined,
): boolean {
  if (!latestTurn || latestTurn.completedAt) {
    return false;
  }
  return requestTurnId == null || requestTurnId === latestTurn.turnId;
}

function hasStructuredUserInputQuestions(payload: Record<string, unknown> | null): boolean {
  const questions = payload?.questions;
  if (!Array.isArray(questions)) {
    return false;
  }
  return questions.some((entry) => {
    if (!entry || typeof entry !== "object") {
      return false;
    }
    const question = entry as Record<string, unknown>;
    const options = Array.isArray(question.options) ? question.options : null;
    return (
      typeof question.id === "string" &&
      typeof question.header === "string" &&
      typeof question.question === "string" &&
      options !== null &&
      options.some((option) => {
        if (!option || typeof option !== "object") {
          return false;
        }
        const optionRecord = option as Record<string, unknown>;
        return (
          typeof optionRecord.label === "string" && typeof optionRecord.description === "string"
        );
      })
    );
  });
}

function resolveLatestProposedPlan(input: {
  readonly proposedPlans: ReadonlyArray<
    Pick<OrchestrationProposedPlan, "id" | "turnId" | "updatedAt" | "implementedAt">
  >;
  readonly latestTurn: Pick<OrchestrationLatestTurn, "turnId"> | null;
}): Pick<OrchestrationProposedPlan, "id" | "turnId" | "updatedAt" | "implementedAt"> | null {
  if (input.latestTurn?.turnId) {
    const matchingTurnPlan = [...input.proposedPlans]
      .filter((plan) => plan.turnId === input.latestTurn?.turnId)
      .toSorted(
        (left, right) =>
          left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
      )
      .at(-1);
    if (matchingTurnPlan) {
      return matchingTurnPlan;
    }
  }

  return (
    [...input.proposedPlans]
      .toSorted(
        (left, right) =>
          left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
      )
      .at(-1) ?? null
  );
}

export function deriveThreadSummaryMetadata(input: {
  readonly messages: ReadonlyArray<Pick<OrchestrationMessage, "role" | "createdAt">>;
  readonly activities: ReadonlyArray<
    Pick<OrchestrationThreadActivity, "createdAt" | "id" | "kind" | "payload" | "sequence"> & {
      readonly turnId?: OrchestrationThreadActivity["turnId"] | undefined;
    }
  >;
  readonly proposedPlans: ReadonlyArray<
    Pick<OrchestrationProposedPlan, "id" | "turnId" | "updatedAt" | "implementedAt">
  >;
  readonly latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "completedAt"> | null;
}): ThreadSummaryMetadata {
  let latestUserMessageAt: string | null = null;
  for (const message of input.messages) {
    if (message.role === "user") {
      latestUserMessageAt = maxIso(latestUserMessageAt, message.createdAt);
    }
  }

  const openApprovals = new Map<string, true>();
  const openUserInputs = new Map<string, true>();
  let delegateReportReadAt: string | null = null;
  let delegationWakeSkippedAt: string | null = null;
  const orderedActivities = [...input.activities].toSorted(compareActivitiesByOrder);
  for (const activity of orderedActivities) {
    const payload = toPayloadRecord(activity.payload);
    const requestId = typeof payload?.requestId === "string" ? payload.requestId : null;
    const detail = typeof payload?.detail === "string" ? payload.detail : undefined;

    if (activity.kind === DELEGATION_REPORT_READ_ACTIVITY_KIND) {
      delegateReportReadAt = maxIso(delegateReportReadAt, activity.createdAt);
      continue;
    }

    if (activity.kind === DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND) {
      delegationWakeSkippedAt = maxIso(delegationWakeSkippedAt, activity.createdAt);
      continue;
    }

    // A request whose turn is over is a leftover, not something the user owes an
    // answer to. See [[isPendingRequestAnswerable]].
    const answerable = isPendingRequestAnswerable(activity.turnId, input.latestTurn);

    if (activity.kind === "approval.requested" && requestId) {
      if (!answerable) {
        continue;
      }
      const requestKind =
        payload?.requestKind === "command" ||
        payload?.requestKind === "file-read" ||
        payload?.requestKind === "file-change"
          ? payload.requestKind
          : requestKindFromRequestType(payload?.requestType);
      if (requestKind) {
        openApprovals.set(requestId, true);
      }
      continue;
    }

    if (activity.kind === "approval.resolved" && requestId) {
      openApprovals.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.approval.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openApprovals.delete(requestId);
      continue;
    }

    if (activity.kind === "user-input.requested" && requestId) {
      if (!answerable) {
        continue;
      }
      if (hasStructuredUserInputQuestions(payload)) {
        openUserInputs.set(requestId, true);
      }
      continue;
    }

    if (activity.kind === "user-input.resolved" && requestId) {
      openUserInputs.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.user-input.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openUserInputs.delete(requestId);
    }
  }

  const latestProposedPlan = resolveLatestProposedPlan({
    proposedPlans: input.proposedPlans,
    latestTurn: input.latestTurn,
  });

  return {
    latestUserMessageAt,
    hasPendingApprovals: openApprovals.size > 0,
    hasPendingUserInput: openUserInputs.size > 0,
    hasActionableProposedPlan: latestProposedPlan?.implementedAt === null,
    delegateReportReadAt,
    delegationWakeSkippedAt,
  };
}
